'use client'
import { useLanguage } from '@/lib/context/LanguageContext'
import { FadeInUp } from '@/components/ui/FadeInUp'
import SectionHeader from '@/components/ui/SectionHeader'
import { MotionCarousel } from '@/components/ui/motion-carousel'
import { vodPlatforms } from '@/lib/data/vodPlatforms'

/**
 * VOD experiment, round 2: one unified carousel of every show across all
 * platforms — no toggle. Each caption carries the platform name so inventory
 * still reads as "Filmnet + Sheida under one roof".
 */
export default function VODShowsCarousel() {
  const { lang } = useLanguage()
  const l = lang as 'en' | 'fa'

  const allShows = vodPlatforms.flatMap((p) =>
    p.shows.map((show) => ({ ...show, platform: l === 'fa' ? p.nameFA : p.name })),
  )

  return (
    <section id="vod" className="py-16 md:py-24 lg:py-32 border-b border-ink/5">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionHeader num="04" label={l === 'fa' ? 'پلتفرم‌های VOD' : 'VOD Platforms'} />

        {/* Heading */}
        <FadeInUp delay={0.05}>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8 md:mb-12">
            <h2 className="font-display font-black text-3xl sm:text-4xl md:text-5xl text-ink leading-tight max-w-2xl">
              {l === 'fa' ? 'تبلیغات در پلتفرم‌های VOD' : 'Advertising on VOD Platforms'}
            </h2>
            <p className="text-muted text-sm max-w-xs leading-relaxed lg:text-right">
              {l === 'fa'
                ? 'دسترسی یکپارچه به موجودی تبلیغاتی پریمیوم VOD'
                : 'Unified access to premium VOD advertising inventory.'}
            </p>
          </div>
        </FadeInUp>

        {/* Unified show carousel */}
        <FadeInUp delay={0.1}>
          <div
            className="rounded-3xl border border-ink/5 p-5 sm:p-8"
            style={{ background: 'rgba(3,53,183,0.04)' }}
          >
            <MotionCarousel
              slides={allShows.map((_, i) => i)}
              options={{ loop: true, direction: l === 'fa' ? 'rtl' : 'ltr' }}
              // Raw manifest keys — MotionCarousel's AssetPicture resolves them
              // and adds the AVIF/WebP <source> variants.
              images={allShows.map((s) => s.img)}
              labels={allShows.map((s) =>
                l === 'fa'
                  ? `${s.titleFA} — ${s.genreFA} · ${s.platform}`
                  : `${s.titleEN} — ${s.genreEN} · ${s.platform}`,
              )}
            />
          </div>
        </FadeInUp>
      </div>
    </section>
  )
}
